import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import React, {useState} from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
} from '@mui/material';

import '../styles/global-tables.scss';
import {SearchableOrder} from '../state/searchable-state';

export type Column<T> = {
  key: keyof T | string;
  label: string;
  render?: (row: T) => React.ReactNode;
  minWidth?: number | string;
  orderKey?: string;
};

export type SimpleTableProps<T> = {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string | number;
  ordering?: SearchableOrder[];
  onOrdering?: (ordering: SearchableOrder) => void;
  onReorder?: (orderedKeys: (string | number)[]) => void;
};

export function SimpleTable<T extends object>({
                                                rows,
                                                columns,
                                                rowKey,
                                                ordering = [],
                                                onOrdering,
                                                onReorder,
                                              }: SimpleTableProps<T>) {
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)
  const reorderEnabled = !!onReorder

  const handleDrop = (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex || !onReorder) {
      setDragIndex(null)
      setDropIndex(null)
      return
    }
    const next = rows.slice()
    const [moved] = next.splice(dragIndex, 1)
    next.splice(targetIndex, 0, moved)
    onReorder(next.map(rowKey))
    setDragIndex(null)
    setDropIndex(null)
  }

  const getColumnOrder = (col: Column<T>) => {
    if (!col.orderKey) return undefined;
    return ordering.find((o) => o.orderBy === col.orderKey) || {
      orderBy: col.orderKey,
      orderValues: [],
      orderDirection: 'NORMAL' as const,
    };
  };

  const handleSortClick = (col: Column<T>) => {
    if (!col.orderKey || !onOrdering) return;

    const current = getColumnOrder(col);
    const nextDirection =
      current?.orderDirection === 'NORMAL'
        ? 'ASCENDING'
        : current?.orderDirection === 'ASCENDING'
          ? 'DESCENDING'
          : 'NORMAL';

    onOrdering({
      orderBy: col.orderKey,
      orderValues: current?.orderValues || [],
      orderDirection: nextDirection,
    });
  };

  return (
    <TableContainer component={Paper} className="table-container">
      <Table size="small" className="custom-table">
        <TableHead>
          <TableRow>
            {reorderEnabled && <TableCell style={{width: 32, padding: 0}}/>}
            {columns.map((col) => {
              const colOrder = getColumnOrder(col);
              const isSortable = !!col.orderKey && !!onOrdering;

              return (
                <TableCell key={col.key as string} style={{ minWidth: col.minWidth }}>
                  {isSortable ? (
                    <TableSortLabel
                      active={colOrder?.orderDirection !== 'NORMAL'}
                      direction={
                        colOrder?.orderDirection === 'DESCENDING' ? 'desc' : 'asc'
                      }
                      onClick={() => handleSortClick(col)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow
              key={rowKey(row)}
              hover
              draggable={reorderEnabled}
              onDragStart={reorderEnabled ? () => setDragIndex(idx) : undefined}
              onDragOver={reorderEnabled ? (e) => {
                e.preventDefault()
                if (dropIndex !== idx) setDropIndex(idx)
              } : undefined}
              onDragLeave={reorderEnabled ? () => {
                if (dropIndex === idx) setDropIndex(null)
              } : undefined}
              onDrop={reorderEnabled ? (e) => {
                e.preventDefault()
                handleDrop(idx)
              } : undefined}
              onDragEnd={reorderEnabled ? () => {
                setDragIndex(null)
                setDropIndex(null)
              } : undefined}
              style={{
                cursor: reorderEnabled ? 'grab' : undefined,
                opacity: dragIndex === idx ? 0.4 : 1,
                background: dropIndex === idx && dragIndex !== idx ? 'rgba(47,199,182,0.1)' : undefined,
              }}
            >
              {reorderEnabled && (
                <TableCell style={{width: 32, padding: '0 4px', color: '#c2cad6'}}>
                  <DragIndicatorIcon fontSize="small"/>
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell key={col.key as string}>
                  {col.render ? col.render(row) : (row[col.key as keyof T] as React.ReactNode)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
